import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Layers, MapPin, FileStack, List, ListTree, Map, Video, Image, Network, Database, Microscope } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useObject, useObjectVersions, useRolledUpRTO, useRolledUpRPO, useObjectRtoDetails, useServiceTable, useRunbook, useSaveRunbook, useScenarios, useObjectsInformation } from '../hooks'
import { ScenarioSelect } from '../components/ScenarioSelect'
import { RunbookTable } from '../components/RunbookTable'
import { ChangeRunbookDialog } from '../components/ChangeRunbookDialog'
import { CloneRunbookDialog } from '../components/CloneRunbookDialog'
import { ObjectRunbookAiDialog } from '../components/ObjectRunbookAiDialog'
import { authHeaders } from '@/features/ai-chat/core/utils/authHeaders'
import { SaveAsMasterDialog } from '../components/SaveAsMasterDialog'
import { RetrieveVersionDialog } from '../components/RetrieveVersionDialog'
import { RequestRecoveryDialog } from '../components/RequestRecoveryDialog'
import { ObjectStatusPair } from '@/components/ui/object-status-pair'
import { StartForensicsDialog } from '@/features/forensics/components/StartForensicsDialog'
import { FloorPlanViewTab } from '../components/FloorPlanViewTab'
import { RecoveryDocumentVideoViewTab } from '../components/RecoveryDocumentVideoViewTab'
import { ObjectImageViewTab } from '../components/ObjectImageViewTab'
import { AnalyticsReportDialog } from '../components/AnalyticsReportDialog'
import { useRequestScan } from '../scan-requests'
import { toast } from '@/hooks/use-toast'
import { AnomalySizeChartDialog } from '../components/AnomalySizeChartDialog'
import { ReconciliationChart } from '@/components/charts/ReconciliationChart'
import { AnomalyHistoryChart } from '../components/AnomalyHistoryChart'
import { AnalyticsHistoryChart } from '../components/AnalyticsHistoryChart'
import type { RunbookTask } from '@/types/entities'
import { DataTable } from '@/components/data/DataTable'
import { createObjectVersionColumns } from '../columns-versions'
import type { ObjectVersion } from '@/types/entities'
import { createServiceTableColumns } from '../columns-service'
import { formatSecondsToDHM } from '@/features/objects/utils/formatTime'
import { formatBytes } from '@/features/objects/utils/formatSize'
import { JsonViewDialog } from '@/components/ui/JsonViewDialog'
import { useMemo, useState, useCallback, useEffect, useRef, Suspense, lazy } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { objectsApi } from '../api'
import { ReactFlowProvider } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { buildIconPath } from '@/lib/imagePaths'
import { EntityIcon } from '@/components/ui/entity-icon'
import { TranslatableText } from '@/components/ui/TranslatableText'
import { useCan } from '@/hooks/useCan'
import { ObjectReviewTabContent, ObjectReviewTabTrigger } from '@/features/hierarchy-advisor/components/ObjectReviewTab'

const VisualizationApp = lazy(() => import('../../visualization/_legacy/App'))
const AppStateProvider = lazy(() =>
    import('../../visualization/_legacy/context/AppStateContext').then((m) => ({
        default: m.AppStateProvider,
    }))
)
// Status indicator component (red/amber use expanding-ring pulse)
// Time Display Box component - styled rectangle for RTO/RPO values
function TimeDisplayBox({ label, value, highlight = false }: { label: string; value: string; highlight?: boolean }) {
    return (
        <div className="flex flex-col">
            <span className="text-[9px] font-medium text-muted-foreground text-right mb-0.5">
                {label}
            </span>
            <div className={`border rounded px-2 py-1.5 text-right min-w-[130px] ${highlight ? 'border-primary bg-primary/5' : 'border-border bg-background'}`}>
                <span className={`text-xs font-medium ${highlight ? 'text-primary' : 'text-foreground'}`}>
                    {value}
                </span>
            </div>
        </div>
    )
}
export function ObjectViewPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { t, i18n } = useTranslation()
    // State declarations (must be before hooks that use them)
    const [jsonDialog, setJsonDialog] = useState<{ open: boolean; title: string; content: string | null }>({
        open: false,
        title: '',
        content: null
    })
    const [rtoDialogOpen, setRtoDialogOpen] = useState(false)
    const [rtoDetailsOpen, setRtoDetailsOpen] = useState(false)
    const [rpoDialogOpen, setRpoDialogOpen] = useState(false)
    const [activeTab, setActiveTab] = useState('versions')
    const [subObjectsSearch, setSubObjectsSearch] = useState('')
    const [runbookAutoUpdate, setRunbookAutoUpdate] = useState(false)
    const [changeRunbookDialogOpen, setChangeRunbookDialogOpen] = useState(false)
    const [cloneRunbookDialogOpen, setCloneRunbookDialogOpen] = useState(false)
    const [saveAsMasterDialogOpen, setSaveAsMasterDialogOpen] = useState(false)
    const [runbookAiDialogOpen, setRunbookAiDialogOpen] = useState(false)
    const [retrieveVersionDialogOpen, setRetrieveVersionDialogOpen] = useState(false)
    const [selectedVersion, setSelectedVersion] = useState<import('@/types/entities').ObjectVersion | null>(null)
    const [requestRecoveryDialogOpen, setRequestRecoveryDialogOpen] = useState(false)
    const [visFullscreen, setVisFullscreen] = useState(false)
    // Escape leaves full screen, so the way out never depends on the toolbar being open — unless
    // something inside has already used it: Escape in a canvas text box puts its text back, and
    // should not also throw the user out of full screen.
    useEffect(() => {
        if (!visFullscreen) return
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented) setVisFullscreen(false) }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [visFullscreen])
    const [analyticsDialog, setAnalyticsDialog] = useState<{ open: boolean; analyticsText: string | null; versionID: number | null }>({ open: false, analyticsText: null, versionID: null })
    const [anomalyChartOpen, setAnomalyChartOpen] = useState(false)
    const [forensicsDialogOpen, setForensicsDialogOpen] = useState(false)
    // Version chosen from a row's menu; null means the header button, which defaults to the
    // newest Full below.
    const [forensicsVersion, setForensicsVersion] = useState<ObjectVersion | null>(null)
    // The page is open to objects.read. Each action is offered only with the permission its API
    // checks (docs/rbac-permissions.md): requesting a recovery or retrieving a version raises a
    // recovery request, forensics starts an environment, and the runbook is part of the object.
    const canRequestRecovery = useCan('recover-requests.create')
    const canStartForensics = useCan('forensics.create')
    // Scan a version again (scan.run). The request shows on the row; a refusal is said in a toast.
    const canRunScan = useCan('scan.run')
    const requestScan = useRequestScan()
    const handleRunScan = useCallback((version: ObjectVersion) => {
        if (version.versionID == null) return
        requestScan.mutate(version.versionID, {
            onSuccess: () => toast({ title: t('scanRequests.requested') }),
            onError: (error: any) => toast({
                title: String(t(`scanRequests.error.${error?.response?.data?.code}`, { defaultValue: error?.response?.data?.message || t('scanRequests.error.generic') })),
                variant: 'destructive',
            }),
        })
    }, [requestScan, t])
    const canEditRunbook = useCan('objects.update')
    const canSaveAsMaster = useCan('master-runbooks.create')
    const canUseAi = useCan('ai.use')
    // Collapse the object header into a slim sticky strip once the user scrolls into the tab
    // content, so long runbooks/versions use the full screen while the object's identity + key
    // figures stay pinned at the top.
    const [headerCollapsed, setHeaderCollapsed] = useState(false)
    useEffect(() => {
        const main = document.querySelector('main')
        if (!main) return
        const onScroll = () => setHeaderCollapsed(main.scrollTop > 120)
        main.addEventListener('scroll', onScroll, { passive: true })
        onScroll()
        return () => main.removeEventListener('scroll', onScroll)
    }, [])
    // Initialize selectedScenario from localStorage, or empty string if not set
    const getInitialScenario = (): string => {
        try {
            const stored = localStorage.getItem('selectedScenario')
            return stored || ''
        } catch {
            return ''
        }
    }
    const [selectedScenario, setSelectedScenario] = useState<string>(getInitialScenario)
    // Save to localStorage whenever selectedScenario changes
    useEffect(() => {
        try {
            if (selectedScenario) {
                localStorage.setItem('selectedScenario', selectedScenario)
            } else {
                localStorage.removeItem('selectedScenario')
            }
        } catch (error) {
            console.warn('Failed to save scenario to localStorage:', error)
        }
    }, [selectedScenario])
    // Hooks - need scenarios first to calculate scenarioParam
    const { data: versions = [], isLoading: isLoadingVersions } = useObjectVersions(id || null)
    const { data: scenarios = [] } = useScenarios(id || null)
    // Find the default scenario (where Default = 1)
    const defaultScenario = scenarios.find((s) => s.isDefault)
    const defaultScenarioId = defaultScenario?.id.toString() || null
    // Use selectedScenario if set, otherwise use the default scenario
    const effectiveScenario = selectedScenario || defaultScenarioId
    const scenarioParam = effectiveScenario || undefined
    // Name of the currently-selected recovery scenario — shown in the collapsed header bar.
    const selectedScenarioName = scenarios.find((s) => s.id.toString() === effectiveScenario)?.name
    const queryClient = useQueryClient()
    // Now we can use scenarioParam in hooks
    const { data: object, isLoading, error } = useObject(id || null, scenarioParam)
    const { data: serviceTableData = [], isLoading: isLoadingServiceTable } = useServiceTable(id || null, scenarioParam)
    const { data: rtoBreakdown = [], isLoading: isLoadingRtoBreakdown } = useRolledUpRTO(id || null, scenarioParam)
    const { data: rpoBreakdown = [], isLoading: isLoadingRpoBreakdown } = useRolledUpRPO(id || null, scenarioParam)
    // Always fetch RTO details when scenario is available (not just when dialog is open) to get endToEndTime
    const { data: rtoDetails, isLoading: isLoadingRtoDetails } = useObjectRtoDetails(id || null, !!scenarioParam, scenarioParam)
    const { data: runbookTasks = [], isLoading: isLoadingRunbook } = useRunbook(id || null, scenarioParam)
    const saveRunbookMutation = useSaveRunbook()
    const { data: objectsInformation } = useObjectsInformation(id || null, scenarioParam)
    
    // Rolled Up RTO comes directly from RolledUpRTORPO.TotalCalculatedRTO filtered by scenario
    // This is fetched via the object query which now includes scenario parameter
    
    // Invalidate and refetch all scenario-dependent queries when scenario changes
    // This ensures the active tab refreshes with new data
    useEffect(() => {
        if (id && scenarioParam) {
            const decodedId = decodeURIComponent(id)
            // Invalidate all queries that depend on scenario
            queryClient.invalidateQueries({ 
                queryKey: ['objects', decodedId, scenarioParam],
                refetchType: 'active' // Only refetch active queries (visible tabs)
            })
            queryClient.invalidateQueries({ 
                queryKey: ['objects', decodedId, 'runbook', scenarioParam],
                refetchType: 'active'
            })
            queryClient.invalidateQueries({ 
                queryKey: ['objects', decodedId, 'service-table', scenarioParam],
                refetchType: 'active'
            })
            queryClient.invalidateQueries({ 
                queryKey: ['objects', decodedId, 'rolled-up-rto', scenarioParam],
                refetchType: 'active'
            })
            queryClient.invalidateQueries({ 
                queryKey: ['objects', decodedId, 'rolled-up-rpo', scenarioParam],
                refetchType: 'active'
            })
            queryClient.invalidateQueries({ 
                queryKey: ['objects', decodedId, 'rto-details', scenarioParam],
                refetchType: 'active'
            })
            queryClient.invalidateQueries({ 
                queryKey: ['objects', decodedId, 'information', scenarioParam],
                refetchType: 'active'
            })
        }
    }, [scenarioParam, id, queryClient])
    
    // Debug logging for runbook tasks (only log when tasks actually change, not on every render)
    useEffect(() => {
        if (runbookTasks.length > 0) {
            console.log('[ObjectViewPage] runbookTasks from query changed', {
                tasksCount: runbookTasks.length,
                firstTaskStartTime: runbookTasks[0]?.startTime,
                firstTaskFinishTime: runbookTasks[0]?.finishTime,
            })
        }
    }, [runbookTasks.length, runbookTasks[0]?.startTime, runbookTasks[0]?.finishTime])
    // Check if this is a Service object
    const isService = object?.objectService === 'Service'
    // Check if this is a Management Only object
    const isManagementOnly = object?.managementOnly === 1
    const handleShowJson = useCallback((title: string, content: string | null) => {
        setJsonDialog({ open: true, title, content })
    }, [])
    const handleRolledUpRTOClick = useCallback(() => {
        setRtoDialogOpen(true)
    }, [])
    const handleRolledUpRPOClick = useCallback(() => {
        setRpoDialogOpen(true)
    }, [])
    const handleEndToEndClick = useCallback(() => {
        setRtoDetailsOpen(true)
    }, [])
    const handleRetrieveVersion = useCallback((version: import('@/types/entities').ObjectVersion) => {
        setSelectedVersion(version)
        setRetrieveVersionDialogOpen(true)
    }, [])
    const handleViewRetrievalInfo = useCallback((version: import('@/types/entities').ObjectVersion) => {
        // TODO: Implement view retrieval information functionality
        console.log('View retrieval info:', version)
    }, [])
    // RunbookTable manages its own state in memory
    // Parent only needs to handle saving - no state synchronization needed
    const handleRunbookSave = useCallback(async (tasksToSave: RunbookTask[]) => {
        console.log('[ObjectViewPage] handleRunbookSave called', {
            tasksCount: tasksToSave.length,
            firstTaskStartTime: tasksToSave[0]?.startTime,
            firstTaskFinishTime: tasksToSave[0]?.finishTime,
            scenario: effectiveScenario,
        })
        if (!id) return
        const decodedId = decodeURIComponent(id)
        const scenarioToSave = effectiveScenario || undefined
        await saveRunbookMutation.mutateAsync({
            objectID: decodedId,
            tasks: tasksToSave,
            scenario: scenarioToSave,
        })
        console.log('[ObjectViewPage] Save mutation completed')
    }, [id, saveRunbookMutation, effectiveScenario])
    // Which of this runbook's tasks have a generated script. Sizes only, no content: the
    // card just needs to know whether to offer the files, and a runbook can carry twenty.
    const { data: runbookCodeTaskNos = [] } = useQuery<number[]>({
        queryKey: ['objects', id, 'runbook-code', effectiveScenario],
        queryFn: async () => {
            const decoded = decodeURIComponent(id || '')
            const r = await fetch(
                `/api/objects/${encodeURIComponent(decoded)}/runbook/code` +
                (effectiveScenario ? `?scenario=${effectiveScenario}` : ''),
                { headers: authHeaders() }
            )
            if (!r.ok) return []
            const body = await r.json()
            const sets = body?.data?.sets ?? []
            return sets.map((x: any) => Number(x.taskNo)).filter((n: number) => Number.isFinite(n))
        },
        enabled: !!id,
        refetchOnWindowFocus: false,
    })

    /** Both AI paths change the runbook server-side, so the page reloads it and its RTO. */
    const handleRunbookRefetch = useCallback(async () => {
        if (!id) return
        await queryClient.invalidateQueries({ queryKey: ['objects', id, 'runbook', scenarioParam] })
        await queryClient.invalidateQueries({ queryKey: ['objects', id] })
    }, [id, scenarioParam, queryClient])

    /**
     * Save a regenerated runbook, scripts and all.
     *
     * This goes straight to the endpoint rather than through the save mutation because
     * generation produces the tasks AND the code that performs them, and the mutation only
     * carries tasks. Saving one without the other would leave commands that run scripts
     * nobody stored.
     */
    const handleRunbookRegenerated = useCallback(async (tasks: RunbookTask[], code: Record<string, unknown>) => {
        if (!id || !effectiveScenario) return
        const decodedId = decodeURIComponent(id)
        const response = await fetch(`/api/objects/${encodeURIComponent(decodedId)}/runbook`, {
            method: 'POST',
            headers: authHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ tasks, scenario: effectiveScenario, code }),
        })
        if (!response.ok) {
            const body = await response.json().catch(() => ({}))
            throw new Error(body?.message || 'Could not save the regenerated runbook')
        }
        await handleRunbookRefetch()
    }, [id, effectiveScenario])

    // Handle changing runbook from master
    const handleChangeRunbookFromMaster = useCallback(async (masterRunbookId: number, customizedTasks?: RunbookTask[]) => {
        if (!id || !scenarioParam) {
            throw new Error('Object ID and scenario are required')
        }
        try {
            const decodedId = decodeURIComponent(id)
            
            // Step 1: Change runbook from master
            await objectsApi.changeRunbookFromMaster(decodedId, masterRunbookId, scenarioParam)
            
            // Step 2: Always save tasks with calculated times (even if not customized)
            // This ensures start/finish times are saved to the database
            // The backend changeRunbookFromMaster inserts tasks with NULL times,
            // so we need to save them with calculated times
            if (customizedTasks && customizedTasks.length > 0) {
                await saveRunbookMutation.mutateAsync({
                    objectID: decodedId,
                    tasks: customizedTasks,
                    scenario: scenarioParam,
                })
            }
            
            // Invalidate and refetch runbook data + the RTO/RPO queries that the top
            // card shows — the ObjectRTO view recomputes from the newly assigned tasks,
            // so End to End / Rolled Up RTO must refresh.
            await queryClient.invalidateQueries({ queryKey: ['objects', id, 'runbook', scenarioParam] })
            queryClient.invalidateQueries({ queryKey: ['objects', decodedId, 'rto-details', scenarioParam] })
            queryClient.invalidateQueries({ queryKey: ['objects', decodedId, 'rolled-up-rto', scenarioParam] })
            queryClient.invalidateQueries({ queryKey: ['objects', decodedId, 'rolled-up-rpo', scenarioParam] })
            queryClient.invalidateQueries({ queryKey: ['objects', decodedId, scenarioParam] })
            console.log('Runbook changed from master', {
                objectID: id,
                masterRunbookId,
                scenario: scenarioParam,
                hasCustomizedTasks: !!customizedTasks
            })
        } catch (error) {
            console.error('Error changing runbook from master:', error)
            throw error
        }
    }, [id, scenarioParam, queryClient, saveRunbookMutation])

    // Handle cloning runbook from another object
    const handleCloneRunbook = useCallback(async (sourceObjectID: string, sourceScenario: string, customizedTasks?: RunbookTask[]) => {
        if (!id || !scenarioParam) {
            throw new Error('Object ID and scenario are required')
        }
        try {
            const decodedId = decodeURIComponent(id)
            const decodedSourceId = decodeURIComponent(sourceObjectID)
            
            // Clone runbook from source object
            await objectsApi.cloneRunbook(decodedId, decodedSourceId, sourceScenario, scenarioParam)
            
            // If customized tasks were provided, save them
            if (customizedTasks && customizedTasks.length > 0) {
                await saveRunbookMutation.mutateAsync({
                    objectID: decodedId,
                    tasks: customizedTasks,
                    scenario: scenarioParam,
                })
            }
            
            // Invalidate and refetch runbook data
            await queryClient.invalidateQueries({ queryKey: ['objects', id, 'runbook', scenarioParam] })
            console.log('Runbook cloned', {
                objectID: id,
                sourceObjectID,
                sourceScenario,
                scenario: scenarioParam,
            })
        } catch (error) {
            console.error('Error cloning runbook:', error)
            throw error
        }
    }, [id, scenarioParam, queryClient, saveRunbookMutation])

    // Handle saving runbook as master
    const handleSaveAsMaster = useCallback(async (name: string, description: string) => {
        if (!id || !scenarioParam) {
            throw new Error('Object ID and scenario are required')
        }
        try {
            const decodedId = decodeURIComponent(id)
            
            // Get current runbook tasks
            const response = await objectsApi.getRunbook(decodedId, scenarioParam)
            const tasks = response.data?.data || response.data || []
            
            if (tasks.length === 0) {
                throw new Error('No runbook tasks to save')
            }
            
            // Save as master runbook
            await objectsApi.saveRunbookAsMaster(decodedId, scenarioParam, name, description)
            
            console.log('Runbook saved as master', {
                objectID: id,
                scenario: scenarioParam,
                name,
            })
        } catch (error) {
            console.error('Error saving runbook as master:', error)
            throw error
        }
    }, [id, scenarioParam])

    const handleShowAnalytics = useCallback((analyticsText: string, version?: ObjectVersion) => {
        setAnalyticsDialog({ open: true, analyticsText, versionID: version?.versionID ?? null })
    }, [])

    const handleShowAnomalyChart = useCallback(() => {
        setAnomalyChartOpen(true)
    }, [])

    // Forensics from a version row: investigate THAT version.
    const handleVersionForensics = (version: ObjectVersion) => {
        setForensicsVersion(version)
        setForensicsDialogOpen(true)
    }

    // The header button has no row context, so it defaults to the newest FULL version —
    // Boot/BootDisk are not the machine and are not what an analyst wants to examine.
    const newestFullVersion = useMemo(
        () => versions.find(v => v.objectSecondary === 'Full') ?? versions[0] ?? null,
        [versions]
    )

    // Create columns for object versions table
    const versionColumns = useMemo(() => {
        if (!object) return []
        return createObjectVersionColumns(
            {
                bootDisk: object.bootDisk,
                fullNo: object.fullNo,
                intNo: object.intNo,
                logNo: object.logNo
            },
            t,
            handleShowJson,
            canRequestRecovery ? handleRetrieveVersion : undefined,
            handleViewRetrievalInfo,
            handleShowAnalytics,
            handleShowAnomalyChart,
            canStartForensics ? handleVersionForensics : undefined,
            canRunScan ? handleRunScan : undefined,
            i18n.language
        )
    }, [object, t, i18n.language, handleShowJson, handleRetrieveVersion, handleViewRetrievalInfo, handleShowAnalytics, handleShowAnomalyChart, canRequestRecovery, canStartForensics, canRunScan, handleRunScan])
    // Create columns for service table
    const serviceColumns = useMemo(() => {
        return createServiceTableColumns(t, handleShowJson)
    }, [t, handleShowJson])
    // Filter service table data based on search
    const filteredServiceTableData = useMemo(() => {
        if (!subObjectsSearch.trim()) {
            return serviceTableData
        }
        const searchLower = subObjectsSearch.toLowerCase().trim()
        return serviceTableData.filter((row) => {
            const sourceID = ((row as any).sourceID || (row as any).objectID || '').toLowerCase()
            const description = (row.description || '').toLowerCase()
            return sourceID.includes(searchLower) || description.includes(searchLower)
        })
    }, [serviceTableData, subObjectsSearch])
    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">{t('objects.loadingObject')}</div>
            </div>
        )
    }
    if (error) {
        return (
            <div className="space-y-6">
                <Button variant="ghost" onClick={() => navigate(-1)}>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {t('common.back')}
                </Button>
                <div className="flex items-center justify-center h-64">
                    <div className="text-red-500">
                        {t('objects.errorLoadingObject')}: {error instanceof Error ? error.message : String(error)}
                    </div>
                </div>
            </div>
        )
    }
    if (!object) {
        return (
            <div className="space-y-6">
                <Button variant="ghost" onClick={() => navigate(-1)}>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {t('common.back')}
                </Button>
                <div className="flex items-center justify-center h-64">
                    <div className="text-muted-foreground">{t('objects.objectNotFound')}</div>
                </div>
            </div>
        )
    }
    return (
        <>
            <div className="space-y-4">
                {/* Back button and Scenario Dropdown - Same line */}
                <div className="flex items-center justify-between">
                    <Button variant="ghost" onClick={() => navigate(-1)}>
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        {t('common.back')}
                    </Button>
                    <ScenarioSelect
                        objectID={id || null}
                        value={effectiveScenario}
                        onValueChange={setSelectedScenario}
                        selectClassName="w-[300px] h-9 font-bold"
                    />
                </div>
                {/* Sticky compact mini-header — overlays the top once the full header scrolls
                    off. It fades in on scroll and -mb removes its flow footprint, so the runbook
                    keeps the full screen and there's no layout jump. */}
                <div
                    aria-hidden={!headerCollapsed}
                    className={`sticky top-0 z-30 -mb-14 transition-opacity duration-150 ${headerCollapsed ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
                >
                    <div className="flex items-center gap-3 rounded-lg border bg-card px-4 py-2 shadow-sm">
                        {object.iconFilename && (
                            <EntityIcon src={buildIconPath(object.iconFilename)} alt="" className="h-6 w-6 shrink-0" />
                        )}
                        <span className="text-sm font-bold shrink-0">{object.objectID || '-'}</span>
                        {object.objectDescription && (
                            <span className="hidden sm:inline text-xs text-muted-foreground truncate">— <TranslatableText text={object.objectDescription} /></span>
                        )}
                        <div className="ml-auto flex items-center gap-4 shrink-0 text-xs">
                            {/* Dot = this object's own scan, network icon = the hierarchy beneath it. */}
                            <ObjectStatusPair
                                variant="ring"
                                scan={object.objectAnalytics}
                                hierarchy={object.objectStatus != null ? Number(object.objectStatus) : null}
                                redHere={object.redHere}
                                redBelow={object.redBelow}
                                indicatorsBelow={object.indicatorsBelow}
                                redBelowIds={object.redBelowIds}
                                indicatorsBelowIds={object.indicatorsBelowIds}
                            />
                            <span className="text-muted-foreground whitespace-nowrap">RTO <span className="font-semibold text-foreground">{object.endToEndTime != null ? formatSecondsToDHM(object.endToEndTime) : '-'}</span></span>
                            {selectedScenarioName && (
                                <span className="text-muted-foreground whitespace-nowrap">Scenario <span className="font-semibold text-foreground"><TranslatableText text={selectedScenarioName} /></span></span>
                            )}
                        </div>
                    </div>
                    {/* Feather below the compact bar: content fades as it scrolls up under it. */}
                    <div className="pointer-events-none absolute inset-x-0 top-full h-12 bg-gradient-to-b from-background to-transparent" aria-hidden />
                </div>
                {/* Object Information — full header, normal flow (scrolls away) */}
                <div className="-mb-10">
                    <div>
                        <Card>
                            <CardContent className="py-4 relative">
                                {/* Request Recovery & Forensics Buttons - Top Right */}
                                <div className="absolute top-4 right-4 flex flex-col gap-2 items-end">
                                    {canRequestRecovery && (
                                        <Button
                                            variant="default"
                                            size="sm"
                                            onClick={() => setRequestRecoveryDialogOpen(true)}
                                            disabled={!effectiveScenario}
                                        >
                                            {t('objects.requestRecovery')}
                                        </Button>
                                    )}
                                    {canStartForensics && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setForensicsDialogOpen(true)}
                                        >
                                            <Microscope className="h-4 w-4 mr-1" />
                                            {t('objects.forensics')}
                                        </Button>
                                    )}
                                    {id && !isService && !isManagementOnly && (
                                        <>
                                            <AnomalyHistoryChart objectID={decodeURIComponent(id)} />
                                            <AnalyticsHistoryChart objectID={decodeURIComponent(id)} />
                                        </>
                                    )}
                                </div>
                                <div className="flex flex-col lg:flex-row gap-4">
                                    {/* Left Side - Object Details */}
                                    <div className="space-y-2 shrink-0">
                                        <div className="flex items-center gap-3">
                                            <Label className="text-xs font-medium text-primary w-28 shrink-0">
                                                {t('objects.objectID')}
                                            </Label>
                                            <div className="flex items-center gap-2">
                                                <p className="text-base font-bold">{object.objectID || '-'}</p>
                                                {object.iconFilename && (
                                                    <EntityIcon
                                                        src={buildIconPath(object.iconFilename)}
                                                        alt=""
                                                        className="h-8 w-8"
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Label className="text-xs font-medium text-muted-foreground w-28 shrink-0">
                                                {t('objects.objectDescription')}
                                            </Label>
                                            <p className="text-sm text-foreground">{object.objectDescription ? <TranslatableText text={object.objectDescription} /> : '-'}</p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Label className="text-xs font-medium text-muted-foreground w-28 shrink-0 flex items-center gap-1">
                                                <Layers className="h-3 w-3" />
                                                {t('objects.classification', 'Classification')}
                                            </Label>
                                            <p className="text-sm text-foreground">
                                                {(object as any).classificationName
                                                    ? <>
                                                        <TranslatableText text={(object as any).classificationName} />
                                                        {(object as any).classificationCategory && (
                                                            <span className="ml-1 text-xs text-muted-foreground">
                                                                · {(object as any).classificationCategory}
                                                            </span>
                                                        )}
                                                    </>
                                                    : '-'}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Label className="text-xs font-medium text-muted-foreground w-28 shrink-0 flex items-center gap-1">
                                                <MapPin className="h-3 w-3" />
                                                {t('objects.objectLocation')}
                                            </Label>
                                            <p className="text-sm text-foreground">{object.objectLocation ? <TranslatableText text={object.objectLocation} /> : '-'}</p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Label className="text-xs font-medium text-muted-foreground w-28 shrink-0">
                                                {t('objects.objectStatus')}
                                            </Label>
                                            <ObjectStatusPair
                                                variant="chips"
                                                scan={object.objectAnalytics}
                                                hierarchy={object.objectStatus != null ? Number(object.objectStatus) : null}
                                                redHere={object.redHere}
                                redBelow={object.redBelow}
                                                indicatorsBelow={object.indicatorsBelow}
                                                redBelowIds={object.redBelowIds}
                                                indicatorsBelowIds={object.indicatorsBelowIds}
                                            />
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Label className="text-xs font-medium text-muted-foreground w-28 shrink-0">
                                                {t('objects.datastore')}
                                            </Label>
                                            {object.datastore === 1 ? (
                                                <img
                                                    src="/images/database.png"
                                                    alt="Datastore"
                                                    className="h-4 w-4"
                                                />
                                            ) : (
                                                <span className="text-muted-foreground">-</span>
                                            )}
                                        </div>
                                    </div>
                                    {/* Middle - RPO and RTO Calculation (positioned at bottom) */}
                                    <div className="flex flex-col items-start justify-end gap-2 -ml-24">
                                        {/* RPO Values - aligned so Rolled Up RPO right edge matches End to End RTO right edge */}
                                        <div className="w-full flex items-end gap-2 justify-end">
                                            <TimeDisplayBox
                                                label={t('objects.rpo')}
                                                value={object.calculatedRPO !== null && object.calculatedRPO !== undefined ? formatSecondsToDHM(object.calculatedRPO) : '-'}
                                            />
                                            <TimeDisplayBox
                                                label={t('objects.realTimeRPO')}
                                                value={object.realTimeRPO !== null && object.realTimeRPO !== undefined ? formatSecondsToDHM(object.realTimeRPO) : '-'}
                                            />
                                            <button
                                                type="button"
                                                onClick={handleRolledUpRPOClick}
                                                className="focus:outline-none"
                                                title={t('objects.rolledUpRPO')}
                                            >
                                                <TimeDisplayBox
                                                    label={t('objects.rolledUpRPO')}
                                                    value={object.rolledUpRPO !== null && object.rolledUpRPO !== undefined ? formatSecondsToDHM(object.rolledUpRPO) : '-'}
                                                    highlight
                                                />
                                            </button>
                                        </div>
                                        {/* Rolled Up RTO above End to End RTO */}
                                        <div className="w-full flex items-end gap-2 justify-end">
                                            <div className="w-[130px]"></div>
                                            <div className="w-[130px]"></div>
                                            <div className="w-[130px]"></div>
                                            <div className="w-[130px]"></div>
                                            <div className="w-4"></div>
                                            <button
                                                type="button"
                                                onClick={handleRolledUpRTOClick}
                                                className="focus:outline-none"
                                                title={t('objects.rolledUpRTO')}
                                            >
                                                <TimeDisplayBox
                                                    label={t('objects.rolledUpRTO')}
                                                    value={object.rolledUpRTO !== null && object.rolledUpRTO !== undefined ? formatSecondsToDHM(object.rolledUpRTO) : '-'}
                                                    highlight
                                                />
                                            </button>
                                        </div>
                                        {/* RTO Calculation (simplified) */}
                                        <div className="w-full flex items-end gap-2 justify-end">
                                            <TimeDisplayBox
                                                label={t('objects.objectSize')}
                                                value={object.objectSize !== null && object.objectSize !== undefined ? formatBytes(object.objectSize) : '-'}
                                            />
                                            <button
                                                type="button"
                                                onClick={handleEndToEndClick}
                                                className="focus:outline-none"
                                                title={t('objects.endToEndRTO')}
                                            >
                                                <TimeDisplayBox
                                                    label={t('objects.endToEndRTO')}
                                                    value={rtoDetails?.endToEndTime !== null && rtoDetails?.endToEndTime !== undefined ? formatSecondsToDHM(rtoDetails.endToEndTime) : (object.endToEndTime !== null && object.endToEndTime !== undefined ? formatSecondsToDHM(object.endToEndTime) : '-')}
                                                    highlight
                                                />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
                {/* Anomaly History - button next to Request Recovery / Forensics */}
                {/* Table Tabs */}
                <div className="mt-6">
                    <Card className="overflow-visible">
                        <CardContent className="p-0 overflow-visible">
                            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                                <TabsList className="w-full justify-start border-b rounded-none h-auto p-0 bg-transparent">
                                    <TabsTrigger
                                        value="versions"
                                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2"
                                    >
                                        <FileStack className="mr-2 h-4 w-4 text-blue-600" />
                                        {t('objects.versions.title')}
                                    </TabsTrigger>
                                    <TabsTrigger
                                        value="visualization"
                                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2"
                                    >
                                        <Network className="mr-2 h-4 w-4 text-blue-600" />
                                        {t('objects.tabs.canvas', 'Canvas')}
                                    </TabsTrigger>
                                    <TabsTrigger
                                        value="subobjects"
                                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2"
                                    >
                                        <ListTree className="mr-2 h-4 w-4 text-blue-600" />
                                        {t('objects.rolledUpView')}
                                    </TabsTrigger>
                                    <TabsTrigger
                                        value="runbook"
                                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2"
                                    >
                                        <List className="mr-2 h-4 w-4 text-blue-600" />
                                        {t('objects.runbook.title')}
                                    </TabsTrigger>
                                    <TabsTrigger
                                        value="floorPlan"
                                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2"
                                    >
                                        <Map className="mr-2 h-4 w-4 text-blue-600" />
                                        {t('objects.tabs.floorPlan')}
                                    </TabsTrigger>
                                    <TabsTrigger
                                        value="documentVideo"
                                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2"
                                    >
                                        <Video className="mr-2 h-4 w-4 text-blue-600" />
                                        {t('objects.tabs.recoveryDocumentVideo')}
                                    </TabsTrigger>
                                    <TabsTrigger
                                        value="image"
                                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2"
                                    >
                                        <Image className="mr-2 h-4 w-4 text-blue-600" />
                                        {t('objects.tabs.objectPicture')}
                                    </TabsTrigger>
                                                                        <TabsTrigger
                                        value="datastores"
                                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2"
                                    >
                                        <Database className="mr-2 h-4 w-4 text-blue-600" />
                                        {t('objects.tabs.datastores', 'Datastores')}
                                    </TabsTrigger>
                                    {/* The Hierarchy Advisor for this service (29 Sep 2026): services only,
                                        hierarchy.review only - the component decides both. */}
                                    <ObjectReviewTabTrigger
                                        isService={isService}
                                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2"
                                    />
                                </TabsList>
                                {/* Object Versions Tab */}
                                <TabsContent value="versions" className="px-4 py-4">
                                    {isLoadingVersions ? (
                                        <div className="flex items-center justify-center h-32">
                                            <div className="text-muted-foreground">{t('objects.versions.loading')}</div>
                                        </div>
                                    ) : isService ? (
                                        <div className="flex items-center justify-center h-32">
                                            <div className="text-muted-foreground">{t('objects.noObjectVersions')}</div>
                                        </div>
                                    ) : isManagementOnly ? (
                                        <div className="flex items-center justify-center h-32">
                                            <div className="text-muted-foreground">{t('objects.managementOnlyMessage')}</div>
                                        </div>
                                    ) : (
                                        <div className="min-w-full">
                                            <DataTable
                                                columns={versionColumns}
                                                data={versions}
                                                className="text-sm leading-5"
                                                enableRowSelection={false}
                                                disablePagination={true}
                                            />
                                        </div>
                                    )}
                                </TabsContent>
                                {/* Visualization Tab */}
                                <TabsContent value="visualization" className={visFullscreen ? "fixed inset-0 z-50 bg-background p-0" : "p-0"}>
                                    {/* Full screen is a toolbar button in the canvas, not a button floated over
                                        it: the top-left corner is where the palette's heading sits. */}
                                    <div className={visFullscreen ? "h-screen w-screen" : "h-[600px] w-full"} style={{ position: 'relative' }}>
                                        {activeTab === 'visualization' && (
                                            <Suspense fallback={
                                                <div className="flex h-full items-center justify-center">
                                                    <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                                                </div>
                                            }>
                                                <AppStateProvider>
                                                    <ReactFlowProvider>
                                                        <VisualizationApp
                                                            scenario="planning"
                                                            view="dependency"
                                                            objectId={id}
                                                            showMainPalette={false}
                                                            scenarioId={scenarioParam || '1'}
                                                            serviceId={scenarioParam || '1'}
                                                            isFullscreen={visFullscreen}
                                                            onToggleFullscreen={() => setVisFullscreen((v) => !v)}
                                                        />
                                                    </ReactFlowProvider>
                                                </AppStateProvider>
                                            </Suspense>
                                        )}
                                    </div>
                                </TabsContent>
                                {/* Sub Objects / Service View Tab */}
                                <TabsContent value="subobjects" className="px-4 py-4">
                                    {isLoadingServiceTable ? (
                                        <div className="flex items-center justify-center h-32">
                                            <div className="text-muted-foreground">{t('objects.service.loading')}</div>
                                        </div>
                                    ) : (
                                        <div className="min-w-full">
                                            <DataTable
                                                columns={serviceColumns}
                                                data={filteredServiceTableData}
                                                searchKey="sourceID"
                                                searchValue={subObjectsSearch}
                                                onSearchChange={setSubObjectsSearch}
                                                searchPlaceholder={t('objects.searchPlaceholder')}
                                                className="text-sm leading-5"
                                                enableRowSelection={false}
                                                disablePagination={true}
                                            />
                                        </div>
                                    )}
                                </TabsContent>
                                {/* Runbook Tab */}
                                <TabsContent value="runbook" className="px-4 py-4">
                                    <div className="space-y-4">
                                        {isLoadingRunbook ? (
                                            <div className="flex items-center justify-center h-32">
                                                <div className="text-muted-foreground">{t('objects.service.loading')}</div>
                                            </div>
                                        ) : (
                                            <>
                                            <RunbookTable
                                                key={`runbook-${id}-${scenarioParam}`}
                                                tasks={runbookTasks || []}
                                                onSave={handleRunbookSave}
                                                autoUpdate={runbookAutoUpdate}
                                                onAutoUpdateChange={setRunbookAutoUpdate}
                                                isSaving={saveRunbookMutation.isPending}
                                                readOnly={!canEditRunbook}
                                                onReplaceWithMaster={canEditRunbook ? () => setChangeRunbookDialogOpen(true) : undefined}
                                                onCloneRunbook={canEditRunbook ? () => setCloneRunbookDialogOpen(true) : undefined}
                                                onSaveAsMaster={canSaveAsMaster ? () => setSaveAsMasterDialogOpen(true) : undefined}
                                                suggestionObjectId={id ? decodeURIComponent(id) : null}
                                                onAiAssist={canEditRunbook && canUseAi ? () => setRunbookAiDialogOpen(true) : undefined}
                                                codeTaskNos={runbookCodeTaskNos}
                                                onFetchTaskCode={async (taskNo: number) => {
                                                    const decoded = decodeURIComponent(id || '')
                                                    const r = await fetch(
                                                        `/api/objects/${encodeURIComponent(decoded)}/runbook/tasks/${taskNo}/code` +
                                                        (effectiveScenario ? `?scenario=${effectiveScenario}` : ''),
                                                        { headers: authHeaders() }
                                                    )
                                                    if (!r.ok) return null
                                                    const body = await r.json()
                                                    return body?.data?.set ?? null
                                                }}
                                            />
                                            </>
                                        )}
                                    </div>
                                </TabsContent>
                                {/* Floor Plan Tab */}
                                <TabsContent value="floorPlan" className="px-4 py-4">
                                    <FloorPlanViewTab
                                        objectID={object?.objectID}
                                        locationID={object?.locationID}
                                        floorPlanX={object?.floorPlanX}
                                        floorPlanY={object?.floorPlanY}
                                    />
                                </TabsContent>
                                {/* Document and Video Tab */}
                                <TabsContent value="documentVideo" className="px-4 py-4">
                                    <RecoveryDocumentVideoViewTab
                                        objectID={id ? decodeURIComponent(id) : undefined}
                                        scenario={scenarioParam}
                                    />
                                </TabsContent>
                                {/* Image Tab */}
                                <TabsContent value="image" className="px-4 py-4">
                                    <ObjectImageViewTab image={objectsInformation?.image} />
                                </TabsContent>
                                {/* Datastores Tab */}
                                                                <TabsContent value="datastores" className="px-4 py-4">
                                    <ReconciliationChart
                                        mainId={id || ''}
                                        scenario={effectiveScenario ? parseInt(effectiveScenario, 10) : null}
                                    />
                                </TabsContent>
                                <ObjectReviewTabContent
                                    isService={isService}
                                    root={id ? decodeURIComponent(id) : ''}
                                    scenario={effectiveScenario || '1'}
                                />
                            </Tabs>
                        </CardContent>
                    </Card>
                </div>
            </div>
            {/* JSON Viewer Dialog */}
            <JsonViewDialog
                open={jsonDialog.open}
                onOpenChange={(open) => setJsonDialog(prev => ({ ...prev, open }))}
                title={jsonDialog.title}
                content={jsonDialog.content}
            />
            {/* Analytics Report Dialog */}
            <AnalyticsReportDialog
                open={analyticsDialog.open}
                onOpenChange={(open) => setAnalyticsDialog(prev => ({ ...prev, open }))}
                analyticsText={analyticsDialog.analyticsText}
                // Looked up from the list each render, not held from the click: a status change made
                // in the dialog refetches the list, and the dialog must show the result.
                version={versions.find((v) => v.versionID != null && v.versionID === analyticsDialog.versionID) ?? null}
                objectId={object?.objectID}
                objectDescription={object?.objectDescription}
                iconFilename={object?.iconFilename}
            />
            {/* Anomaly Size Chart Dialog */}
            <AnomalySizeChartDialog
                open={anomalyChartOpen}
                onOpenChange={setAnomalyChartOpen}
                objectID={id ? decodeURIComponent(id) : ''}
            />
            {/* End-to-End RTO Details Dialog */}
            <Dialog open={rtoDetailsOpen} onOpenChange={setRtoDetailsOpen}>
                <DialogContent className="max-w-lg w-full max-h-[70vh]">
                    <DialogHeader>
                        <DialogTitle>{t('objects.endToEndRTO')}</DialogTitle>
                        <DialogDescription>{t('objects.endToEndRTO')}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="border rounded-lg overflow-hidden">
                            <div className="grid grid-cols-4 bg-muted/60 text-xs font-semibold text-muted-foreground px-3 py-2">
                                <span>Egress</span>
                                <span>Link</span>
                                <span>Engineer</span>
                                <span className="text-right">End to End</span>
                            </div>
                            <div className="divide-y divide-border">
                                {isLoadingRtoDetails ? (
                                    <div className="p-3 text-sm text-muted-foreground">{t('objects.loadingObject')}</div>
                                ) : (
                                    <div className="grid grid-cols-4 px-3 py-2 text-sm">
                                        <span>{rtoDetails?.egressTime !== null && rtoDetails?.egressTime !== undefined ? formatSecondsToDHM(rtoDetails.egressTime) : '-'}</span>
                                        <span>{rtoDetails?.linkTime !== null && rtoDetails?.linkTime !== undefined ? formatSecondsToDHM(rtoDetails.linkTime) : '-'}</span>
                                        <span>{rtoDetails?.engineerRTO !== null && rtoDetails?.engineerRTO !== undefined ? formatSecondsToDHM(rtoDetails.engineerRTO) : '-'}</span>
                                        <span className="text-right">{rtoDetails?.endToEndTime !== null && rtoDetails?.endToEndTime !== undefined ? formatSecondsToDHM(rtoDetails.endToEndTime) : '-'}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            {/* Rolled Up RTO Breakdown Dialog */}
            <Dialog open={rtoDialogOpen} onOpenChange={setRtoDialogOpen}>
                <DialogContent className="max-w-4xl w-full max-h-[80vh]">
                    <DialogHeader>
                        <DialogTitle>{t('objects.rolledUpRTO')}, {object?.objectID || id || ''}</DialogTitle>
                        <DialogDescription>{t('objects.rolledUpRTO')}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="flex justify-end pr-3">
                            <div className="text-sm text-muted-foreground flex items-center gap-1">
                                <span>{t('objects.rolledUpRTO')}:</span>
                                <span className="font-semibold text-foreground">
                                    {object?.rolledUpRTO !== null && object?.rolledUpRTO !== undefined ? formatSecondsToDHM(object.rolledUpRTO) : '-'}
                                </span>
                            </div>
                        </div>
                        <div className="border rounded-lg overflow-hidden">
                            <div className="grid grid-cols-[150px_44px_1fr_80px_100px] bg-muted/60 text-xs font-semibold text-muted-foreground px-3 py-2 gap-3">
                                <span>Object ID</span>
                                <span className="sr-only">Icon</span>
                                <span>Description</span>
                                <span>Source Layer</span>
                                <span className="text-right">RTO</span>
                            </div>
                            <div className="max-h-64 overflow-auto divide-y divide-border">
                                {isLoadingRtoBreakdown ? (
                                    <div className="p-3 text-sm text-muted-foreground">{t('objects.loadingObject')}</div>
                                ) : rtoBreakdown.length === 0 ? (
                                    <div className="p-3 text-sm text-muted-foreground">No data</div>
                                ) : (
                                    rtoBreakdown.map((row, idx) => (
                                        <div key={`${row.objectID}-${row.layerID}-${idx}`} className="grid grid-cols-[150px_44px_1fr_80px_100px] px-3 py-2 text-sm gap-3 items-center">
                                            <span className="truncate" title={row.objectID}>{row.objectID}</span>
                                            <EntityIcon src={row.iconFilename ? buildIconPath(row.iconFilename) : null} alt="" className="h-6 w-6" />
                                            <span className="truncate" title={row.objectDescription}>{row.objectDescription ? <TranslatableText text={row.objectDescription} /> : '-'}</span>
                                            <span>{row.layerID ?? '-'}</span>
                                            <span className="text-right">{row.rto !== null && row.rto !== undefined ? formatSecondsToDHM(row.rto) : '-'}</span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            {/* Rolled Up RPO Breakdown Dialog */}
            <Dialog open={rpoDialogOpen} onOpenChange={setRpoDialogOpen}>
                <DialogContent className="max-w-4xl w-full max-h-[80vh]">
                    <DialogHeader>
                        <DialogTitle>{t('objects.rolledUpRPO')} {object?.objectID || id || ''}</DialogTitle>
                        <DialogDescription>{t('objects.rolledUpRPO')}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-2">
                            <div className="flex justify-end pr-3">
                                <div className="text-sm text-muted-foreground flex items-center gap-1">
                                    <span>{t('objects.rolledUpRPO')}:</span>
                                    <span className="font-semibold text-foreground">
                                        {object?.rolledUpRPO !== null && object?.rolledUpRPO !== undefined ? formatSecondsToDHM(object.rolledUpRPO) : '-'}
                                    </span>
                                </div>
                            </div>
                            <div className="border rounded-lg overflow-hidden">
                                <div className="grid grid-cols-[150px_44px_1fr_100px_100px] bg-muted/60 text-xs font-semibold text-muted-foreground px-3 py-2 gap-3">
                                    <span>Object ID</span>
                                    <span className="sr-only">Icon</span>
                                    <span>Description</span>
                                    <span className="text-center">Source Layer</span>
                                    <span className="text-right">RPO</span>
                                </div>
                                <div className="max-h-64 overflow-auto divide-y divide-border">
                                    {isLoadingRpoBreakdown ? (
                                        <div className="p-3 text-sm text-muted-foreground">{t('objects.loadingObject')}</div>
                                    ) : rpoBreakdown.length === 0 ? (
                                        <div className="p-3 text-sm text-muted-foreground">No data</div>
                                    ) : (
                                        rpoBreakdown.map((row, idx) => (
                                            <div key={`${row.objectID}-${idx}`} className="grid grid-cols-[150px_44px_1fr_100px_100px] px-3 py-2 text-sm gap-3 items-center">
                                                <span className="truncate" title={row.objectID}>{row.objectID}</span>
                                                <EntityIcon src={row.iconFilename ? buildIconPath(row.iconFilename) : null} alt="" className="h-6 w-6" />
                                                <span className="truncate" title={row.objectDescription}>{row.objectDescription ? <TranslatableText text={row.objectDescription} /> : '-'}</span>
                                                <span className="text-center">{row.sourceLayer ?? '-'}</span>
                                                <span className="text-right">{row.rpo !== null && row.rpo !== undefined ? formatSecondsToDHM(row.rpo) : '-'}</span>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
                </Dialog>
                {/* Change Runbook Dialog */}
                {id && scenarioParam && (
                    <>
                        <ChangeRunbookDialog
                            open={changeRunbookDialogOpen}
                            onOpenChange={setChangeRunbookDialogOpen}
                            objectID={id}
                            scenario={scenarioParam}
                            onApply={handleChangeRunbookFromMaster}
                        />
                        <CloneRunbookDialog
                            open={cloneRunbookDialogOpen}
                            onOpenChange={setCloneRunbookDialogOpen}
                            objectID={id}
                            scenario={scenarioParam}
                            onApply={handleCloneRunbook}
                        />
                        <ObjectRunbookAiDialog
                            open={runbookAiDialogOpen}
                            onOpenChange={setRunbookAiDialogOpen}
                            objectId={decodeURIComponent(id)}
                            scenario={effectiveScenario ? parseInt(effectiveScenario, 10) : null}
                            onRegenerated={handleRunbookRegenerated}
                        />
                        <SaveAsMasterDialog
                            open={saveAsMasterDialogOpen}
                            onOpenChange={setSaveAsMasterDialogOpen}
                            objectID={id}
                            scenario={scenarioParam}
                            onSave={handleSaveAsMaster}
                        />
                        <RetrieveVersionDialog
                            open={retrieveVersionDialogOpen}
                            onOpenChange={setRetrieveVersionDialogOpen}
                            version={selectedVersion}
                            objectID={id}
                        />
                        <RequestRecoveryDialog
                            open={requestRecoveryDialogOpen}
                            onOpenChange={setRequestRecoveryDialogOpen}
                            objectID={id}
                            scenario={scenarioParam ? Number(scenarioParam) : null}
                        />
                        <StartForensicsDialog
                            open={forensicsDialogOpen}
                            onOpenChange={(o) => { setForensicsDialogOpen(o); if (!o) setForensicsVersion(null) }}
                            objectId={id || ''}
                            objectVersionName={(forensicsVersion ?? newestFullVersion)?.objectName ?? null}
                            objectVersionId={(forensicsVersion ?? newestFullVersion)?.versionID ?? null}
                            objectDescription={object?.objectDescription}
                            vaultId={(forensicsVersion ?? newestFullVersion)?.vaultID ?? null}
                        />
                    </>
                )}
        </>
    )
}
